import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import "react-loading-skeleton/dist/skeleton.css";

import {
  Home,
  Login,
  SetupPin,
  ResetPassword,
  AccountInfo,
  CashoutAgent,
  CashoutMomo,
  DepositAgent,
  LangUpdate,
  TransactionView,
  WalletView,
  PinUpdate,
  Signup,
  Ads,
  AllUsers,
  MerchantPayment,
  UserDetails,
  SendMoney,
  SignupChatPage,
  Rates,
  Currencies,
  MopayPayment,
  Setting,
  MerchantPortalLogin,
  MerchantPortalVerifyOtp,
  PortalLayout,
  MerchantPortalDashboard,
  MerchantPortalTransactions,
  MerchantPortalSettings,
  MerchantPortalSubAccounts,
} from "./pages";
import Protected from "./components/auth/Protected";
import PortalProtected from "./components/auth/PortalProtected";
import { PortalAuthProvider } from "./contexts/PortalAuthContext";
import "./App.css";
import { auth } from "./firebase/firebase-config";
import { MyContext, useStateContext } from "./contexts/ContextProvider";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DepositMomo from "./pages/momo-send";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TableContextProvider, useTableContext } from "./contexts/TableContext";
import { getCurrencies } from "./services/currencies";
import { getIdToken } from "firebase/auth";
import KycPage from "./pages/kyc";

const App = () => {
  const { setCurrentColor, setCurrentMode, currentMode, activeMenu } =
    useStateContext();
  const [user, loading, error] = useAuthState(auth);
  const [authToken, setAuthToken] = useState("");
  const [currencyCode, setCurrencyCode] = React.useState([]);
  const { setLoading } = useTableContext();

  const fetchCurrencies = () => {
    setLoading(true);
    getCurrencies()
      .then((res) => {
        if (res.status_code === 200) {
          setCurrencyCode(res.data);
        } else {
          toast.error(`${res.message}`);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
        toast.error(`${err}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const currentThemeColor = localStorage.getItem("colorMode");
    const currentThemeMode = localStorage.getItem("themeMode");

    if (currentThemeColor && currentThemeMode) {
      setCurrentColor(currentThemeColor);
      setCurrentMode(currentThemeMode);
    }

    fetchCurrencies();
  }, []);

  return loading ? (
    ""
  ) : (
    <MyContext.Provider value={{ currencyCode, authToken }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <BrowserRouter>
          <PortalAuthProvider>
          <Routes>
            {/* merchant portal */}
            <Route path="/merchant-portal/login" element={<MerchantPortalLogin />} />
            <Route path="/merchant-portal/verify-otp" element={<MerchantPortalVerifyOtp />} />
            <Route
              path="/merchant-portal"
              element={
                <PortalProtected>
                  <PortalLayout />
                </PortalProtected>
              }
            >
              <Route index element={<MerchantPortalDashboard />} />
              <Route path="dashboard" element={<MerchantPortalDashboard />} />
              <Route path="transactions" element={<MerchantPortalTransactions />} />
              <Route path="sub-accounts" element={<MerchantPortalSubAccounts />} />
              <Route path="settings" element={<MerchantPortalSettings />} />
            </Route>
            {/* auth  */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup/:uid" element={<Signup />} />
            <Route path="/signup-chat/:uid" element={<SignupChatPage />} />
            <Route path="/user-setup/:uid" element={<SetupPin />} />
            <Route path="/momo-deposit/:uid" element={<DepositMomo />} />
            <Route path="/account-info/:uid" element={<AccountInfo />} />
            <Route path="/cashout-agent/:uid" element={<CashoutAgent />} />
            <Route path="/send-money/:uid" element={<SendMoney />} />
            <Route path="/cashout-momo/:uid" element={<CashoutMomo />} />
            <Route path="/deposit-agent/:uid" element={<DepositAgent />} />
            <Route path="/merchant-pay/:uid" element={<MerchantPayment />} />
            <Route path="/mopay-payment/:uid" element={<MopayPayment />} />
            <Route path="/kyc/:uid" element={<KycPage />} />
            <Route path="/lang-update/:uid/:language" element={<LangUpdate />} />
            <Route path="/transaction-view/:uid" element={<TransactionView />} />
            <Route path="/pin-update/:uid" element={<PinUpdate />} />
            <Route path="/wallet/:uid" element={<WalletView />} />
            <Route path="/ads/:uid" element={<Ads />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* main  */}
            <Route
              path="/"
              element={
                <Protected isLoggedIn={user}>
                  <Home />
                </Protected>
              }
            />
            <Route
              path="/users"
              element={
                <Protected isLoggedIn={user}>
                  <TableContextProvider>
                    <AllUsers />
                  </TableContextProvider>
                </Protected>
              }
            />
            <Route
              path="/home"
              element={
                <Protected isLoggedIn={user}>
                  <TableContextProvider>
                    <Home />
                  </TableContextProvider>
                </Protected>
              }
            />

            <Route
              path="/users/:userId"
              element={
                <Protected isLoggedIn={user}>
                  <UserDetails />
                </Protected>
              }
            />
            <Route
              path="/exchange-rates"
              element={
                <TableContextProvider>
                  <Protected isLoggedIn={user}>
                    <Rates />
                  </Protected>
                </TableContextProvider>
              }
            />

            <Route
              path="/currencies"
              element={
                <Protected isLoggedIn={user}>
                  <TableContextProvider>
                    <Currencies />
                  </TableContextProvider>
                </Protected>
              }
            />
            <Route
              path="/settings"
              element={
                <Protected isLoggedIn={user}>
                  <TableContextProvider>
                    <Setting />
                  </TableContextProvider>
                </Protected>
              }
            />
          </Routes>
          </PortalAuthProvider>
        </BrowserRouter>
        <ToastContainer />
      </LocalizationProvider>
    </MyContext.Provider>
  );
};

export default App;
